// All marketing copy, sourced from the wiki (Concept, Brand Pack, USPs,
// Features, Use Cases, Site Pages). Consumer value only — the business
// mechanics/economics stay off the page by design.

export const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Calculator", href: "#calculator" },
  { label: "Try the demo", href: "#demo" },
  { label: "Community", href: "#community" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

// The four moves of the earn-and-spend loop (from Concept "How It Works").
export const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Weigh in",
    body: "Snap a photo of your scale or type the number. Under three seconds, no smart scale required.",
    icon: "scan",
  },
  {
    step: "02",
    title: "Earn",
    body: "Every reading converts real progress into calories across three streams — instantly, before you've logged a single meal.",
    icon: "coins",
  },
  {
    step: "03",
    title: "Watch your bank grow",
    body: "Your balance animates up the moment you weigh in, then a plain-English receipt shows exactly where every calorie came from.",
    icon: "trending",
  },
  {
    step: "04",
    title: "Spend it",
    body: "Snap a photo of your meal and the app logs it for you, then suggests dishes that fit what's in your bank. It's not a budget you're rationed — it's permission you've earned.",
    icon: "wallet",
  },
];

// Consumer-facing capabilities (from Platform Features, reframed as benefits).
export const FEATURES = [
  {
    title: "Your calorie bank, front and centre",
    body: "Your spendable balance is the biggest thing on the screen, updated the instant you weigh in. Always know what you've earned before you decide what to eat.",
    icon: "wallet",
  },
  {
    title: "Snap your food, we'll log it",
    body: "Point your camera at a meal and the AI names it, spots the brand and asks a quick question if it's unsure — then fills in the calories for you. Prefer to type? That works too.",
    icon: "camera",
  },
  {
    title: "Meal ideas that fit your bank",
    body: "Tell it how you like to eat and it suggests recipes and restaurant orders matched to the calories you've actually got — so 'what's for dinner?' answers itself.",
    icon: "utensils",
  },
  {
    title: "Friends, groups & milestone cheers",
    body: "Invite a friend, join groups for your town, your favourite food or your sport, and let every milestone auto-post for hearts and thumbs — cheers only, never comments or judgement.",
    icon: "users",
  },
  {
    title: "Streaks, badges & progress photos",
    body: "Show up daily to build a streak and unlock badges at 7, 30 and 100 days. Optional front and side progress photos line up with an on-screen body guide, so you can compare any two days from both angles.",
    icon: "flame",
  },
  {
    title: "Every calorie, traceable to the maths",
    body: "A line-by-line receipt follows every weigh-in, and you can tap any earning to see the exact formula with your own numbers in it. No black box, no mystery points — just numbers that add up.",
    icon: "shield",
  },
];

// Three audiences (Use Cases / Target Market — user value only, no market strategy).
export const PERSONAS = [
  {
    id: "quit",
    tab: "Tried & quit an app",
    headline: "You lost the weight. The app acted like nothing happened.",
    pain: "You logged every meal, watched the scale move, and your tracker treated it like a rounding error. It felt like self-punishment with no payoff — so you quit.",
    gain: "Now every weigh-in directly inflates a food budget you can spend, and a group of people who get it cheers each milestone on. Your progress is finally visible, rewarded, and yours.",
    accent: "teal" as const,
    photo: "/photos/persona-quit.jpg",
  },
  {
    id: "glp1",
    tab: "On GLP-1 medication",
    headline: "The medication is working. Your app hasn't noticed.",
    pain: "The scale is dropping but nothing in your day reflects it. Your motivation lives and dies with the prescription, with no daily habit to anchor to.",
    gain: "FitBuddy turns each loss into a daily reward and a habit to show up for — the layer your prescription doesn't give you. A body-composition view helps you protect muscle, and meal ideas keep protein on the plate.",
    accent: "violet" as const,
    photo: "/photos/persona-glp1.jpg",
  },
  {
    id: "fitness",
    tab: "Already track fitness",
    headline: "You gamify your runs. Your nutrition app is a joyless red bar.",
    pain: "You close rings and chase kudos all day, then open a food app that only ever tells you when you've failed.",
    gain: "This is the nutrition counterpart you've been missing — scale progress treated as an achievement worth rewarding, with streaks, badges and group leaderboards to chase, in a system as data-rich as the one you already love.",
    accent: "teal" as const,
    photo: "/photos/persona-fitness.jpg",
  },
];

// Pre-download questions (from the copy brief, grounded in the wiki).
export const FAQS = [
  {
    q: "How do the three earning streams actually work?",
    a: "You earn 2,000 calories per kilogram for a new all-time low, 2,000 per kilogram when your rolling three-day average hits a new best, and 500 calories for every 24 hours since your last weigh-in. Most weigh-ins trigger more than one stream, so the calories add up fast — and every credit is calculated from a formula you can inspect.",
  },
  {
    q: "Do I really have to log every meal by hand?",
    a: "No — that's the part everyone hates, so we fixed it. Photograph your plate and the AI identifies the food, spots the brand and fills in the calories, asking a quick question only when it's unsure. It'll even suggest meals and restaurant orders that fit the calories you've banked. Manual entry is always there as a fallback.",
  },
  {
    q: "What if the scale doesn't move for a week?",
    a: "You still earn 500 calories every day just for logging, and your streak and badges keep climbing. Consistency pays out on its own, and because a second stream watches your three-day average, real progress that's invisible day-to-day often still shows up and rewards you.",
  },
  {
    q: "Is it social — will people see my actual weight?",
    a: "Only what you choose. Invite friends or join groups for your town, your favourite food or your sport, and share as little as 'calories earned' rather than a number on the scale. Milestones auto-post for hearts and thumbs-up — reactions only, never comments — so it's encouragement, never a place to be judged.",
  },
  {
    q: "Do I need a smart scale?",
    a: "No. Photograph any ordinary scale and FitBuddy reads the number, or type it in manually. Both routes feed the exact same earning streams and give you full credit.",
  },
  {
    q: "Is this real science or just gamification?",
    a: "A kilogram of body fat stores roughly 7,700 calories — that's the real, physical number the whole idea is built on. Your earnings are transparent arithmetic you can check, and the reward loop is the same habit-forming pattern proven at scale by apps like Duolingo.",
  },
  {
    q: "How much does it cost?",
    a: "Nothing. FitBuddy is free to use, with everything included: every weigh-in, all three streams, AI food logging, meal ideas, trend charts, friends and groups. There's no subscription, no card needed, no locked features and no ads.",
  },
  {
    q: "What happens to my weight data?",
    a: "You give explicit, plain-English consent before you log anything, and your data is yours — you can export all of it whenever you like to share with a GP, dietitian or trainer, or just to keep.",
  },
];

// "The science" points (Concept + Brand Pack — the credibility layer).
export const SCIENCE = [
  {
    stat: "7,700",
    unit: "calories per kg",
    label: "The real energy stored in a kilogram of body fat — the physical fact your calorie bank is grounded in, not arbitrary game points.",
  },
  {
    stat: "3",
    unit: "independent streams",
    label: "All-time low, three-day average and time elapsed — calculated and paid out at every single weigh-in.",
  },
  {
    stat: "100%",
    unit: "traceable maths",
    label: "Tap any earning to see the exact formula with your own numbers substituted in. Nothing is hidden behind a black box.",
  },
];

// The social/community layer (Features + USP 6 — day-one retention, no toxicity).
export const COMMUNITY = [
  {
    title: "Bring a friend",
    body: "Invite someone with a link and keep each other honest. Set your own privacy — share 'calories earned', not your weight — for accountability without exposure.",
    icon: "userPlus",
  },
  {
    title: "Find your people",
    body: "Join groups for your town, your favourite food or your sport. It's a cohort that gets you, not another generic weight-loss feed.",
    icon: "users",
  },
  {
    title: "Milestones that get cheered",
    body: "Hit a new low or a streak milestone and it auto-posts to your groups for hearts and thumbs-up. Reactions only — no comments, no negativity.",
    icon: "party",
  },
  {
    title: "Climb the leaderboard",
    body: "Friendly rankings inside your friend and community groups turn everyday progress into a little healthy competition worth showing up for.",
    icon: "trophy",
  },
];

// Everything included, free (Concept / Features). One list, no tiers.
export const ALL_PERKS = [
  "Weigh in and earn across all three streams",
  "Your live calorie bank, updated the instant you weigh in",
  "AI food logging by photo, barcode or voice",
  "Meal ideas matched to what's in your bank",
  "Full history with 30-day trend charts",
  "Streaks, badges and streak grace days",
  "Body-composition tracking and workouts",
  "Friends, groups, CSV export, no ads",
];

export const FOOTER_LINKS = {
  Product: [
    { label: "How it works", href: "#how-it-works" },
    { label: "See what it banks", href: "#calculator" },
    { label: "Try the demo", href: "#demo" },
    { label: "Community", href: "#community" },
    { label: "Pricing", href: "#pricing" },
    { label: "The science", href: "#science" },
  ],
  Company: [
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
    { label: "Support", href: "/support" },
    { label: "FAQ", href: "#faq" },
  ],
  Legal: [
    { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" },
    { label: "Cookies", href: "/cookies" },
    { label: "Delete account", href: "/delete-account" },
  ],
};
